import { Job, JobType } from '@flatfile/api-internal/api'
import React, { useState } from 'react'
import { Button, Checkbox, Input, Text } from '@flatfile/baseline'
import { useNavigate } from 'react-router-dom'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import { useCollection } from '../../resources/hooks/useCollection'
import { useCreateResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import getCollection from '../../resources/support/getCollection'
import { mixpanel } from '../../support/mixpanel'
import { logger } from '../../utils/logging'
import { FirstLoginDemoLoading } from './FirstLoginDemoLoading'
import vgaKineticBg from '/images/vga-kinetic-export.png'
import { NewFlatfileLogoFull } from '../../assets/new-flatfile-logo-full'
import { useEntitlements } from '../../hooks/useEntitlements'
import { addDays, format } from 'date-fns'
import { WistiaPlayer } from '@wistia/wistia-player-react'

export const FirstLoginPage: React.FC = () => {
  const navigate = useNavigate()
  const [configureDemoJob, setConfigureDemoJob] = useState<Job | undefined>(undefined)
  const [touched, setTouched] = useState(false)
  const { environment: activeEnvironment } = useActiveEnvironment()
  const { identity } = useIdentity()
  const isAccountNameAnEmailAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity.account.name)
  const [companyName, setCompanyName] = useState(isAccountNameAnEmailAddress ? '' : identity.account.name)
  const [errorMessage, setErrorMessage] = useState('')
  const [updateAccount] = useUpdateResource('ACCOUNTS')
  const [updateOnboarding] = useUpdateResource('ONBOARDINGS')
  const [createJob] = useCreateResource('JOBS')
  const jobs = useCollection('JOBS')
  const companyResearchJobs = jobs.filter((job: Job) => job.operation === 'company-research')
  const onboarding = useCollection('ONBOARDINGS')[0]
  const apps = useCollection('APPS')
  const onboardingApp = apps.find((app) => app.name === 'Onboarding')
  const demoApp = apps.find((app) => app.metadata?.isDemo === true)
  const [selectedOption, setSelectedOption] = useState<string>(demoApp ? 'demo' : 'template')
  const [isLoading, setIsLoading] = useState(false)
  const [isAgreedToTerms, setIsAgreedToTerms] = useState(false)
  const { hasEntitlement, getEntitlementMetadata } = useEntitlements()

  const handleContinue = async () => {
    setCompanyName(companyName.trim())
    if (!companyName) return

    let rerunCompanyResearchJobId = undefined
    if (!companyResearchJobs || companyResearchJobs.length === 0 || companyName !== identity.account.name) {
      // re-run company research job
      const rerunCompanyResearchJob = await createJob({
        type: JobType.Onboarding,
        operation: 'company-research',
        trigger: 'immediate',
        source: onboarding.id,
        environmentId: activeEnvironment.id,
        config: {
          companyName,
        },
        mode: 'foreground',
        // wait for existing company research job to complete before starting new one
        predecessorIds: companyResearchJobs.length > 0 ? [companyResearchJobs[0].id] : [],
      })
      getCollection('JOBS').refresh()
      rerunCompanyResearchJobId = rerunCompanyResearchJob.id
    }

    mixpanel.track('Click Continue', { step: 'Select Path', selectedPath: selectedOption })
    setIsLoading(true)
    setErrorMessage('')

    try {
      // Update account with company name
      await updateAccount({
        name: companyName,
      }).catch((error) => {
        logger.error('Failed to update account with company name', { error })
        // Continue with the flow even if account update fails
      })

      if (selectedOption === 'template') {
        // Set onboarding flow to BUILDMODE for autobuild path
        if (onboarding?.id) {
          try {
            await updateOnboarding(onboarding.id, {
              flow: 'BUILDMODE',
              currentStep: 0,
            })
          } catch (error) {
            logger.error('Failed to set onboarding flow to BUILDMODE', { error })
            // Continue with navigation even if onboarding update fails
          }
        }

        // Navigate to autobuild flow with the new app ID and welcome tooltip parameter
        navigate(`/apps/custom/${onboardingApp?.id}/build/configure?welcome=true`)
      } else if (selectedOption === 'demo') {
        // Custom demo option - navigate to space list
        navigate(`/apps/custom/${demoApp?.id}/list`)
      } else {
        if (onboarding?.id) {
          try {
            await updateOnboarding(onboarding.id, {
              flow: 'WORKFLOW',
              currentStep: 0,
            })
          } catch (error) {
            logger.error('Failed to set onboarding flow to WORKFLOW', { error })
            // Continue with navigation even if onboarding update fails
          }
        }
        // Sample data option
        if (!activeEnvironment) {
          logger.error('Missing active environment')
          setErrorMessage('Missing active environment. Please refresh and try again.')
          setIsLoading(false)
          return
        }

        if (!onboardingApp) {
          logger.error('Missing onboarding app')
          setErrorMessage('Missing onboarding app. Please refresh and try again.')
          setIsLoading(false)
          return
        }

        const predecessorIds = rerunCompanyResearchJobId
          ? [rerunCompanyResearchJobId]
          : companyResearchJobs.length > 0
            ? [companyResearchJobs[0].id]
            : []
        const configureDemoJob: Job = await createJob({
          type: JobType.Onboarding,
          operation: 'configure-demo',
          trigger: 'immediate',
          source: onboarding.id,
          environmentId: activeEnvironment.id,
          config: {
            companyName,
          },
          mode: 'foreground',
          predecessorIds,
        })
        getCollection('JOBS').refresh()

        if (!configureDemoJob) {
          setErrorMessage('Failed to create onboarding job. Please try again.')
          setIsLoading(false)
          return
        }

        setConfigureDemoJob(configureDemoJob)
      }
    } catch (error) {
      logger.error('Failed to create app', { error })
      setErrorMessage('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBlur = () => {
    setTouched(true)
  }

  const handleChangePath = (path: string) => {
    mixpanel.track('Change Path', { path })
    setSelectedOption(path)
  }

  const isCompanyInvalid = touched && !companyName.trim()

  const trialEnabled = hasEntitlement('trialEnabled')
  const trialMetadata = getEntitlementMetadata('trialEnabled') as unknown as { expiresAt: string } | undefined
  const trialEndDate = trialMetadata ? new Date(trialMetadata.expiresAt) : addDays(new Date(), 14)
  const trialEndDateText = trialEnabled ? format(trialEndDate, 'MM/dd/yy') : undefined

  return (
    <div
      className='h-screen w-screen flex items-center justify-center bg-cover bg-center'
      style={{
        backgroundImage: `url(${vgaKineticBg})`,
      }}
    >
      {configureDemoJob ? (
        <FirstLoginDemoLoading jobId={configureDemoJob.id} />
      ) : (
        <div className='w-full bg-[#F0F0F0] rounded-lg shadow-xl p-8 m-4 max-w-[754px]'>
          <div className='w-full h-full rounded-lg'>
            <div className='flex gap-10'>
              <div className='flex flex-col gap-5 grow basis-full'>
                <NewFlatfileLogoFull />
                <div className='flex flex-col'>
                  <span className='text-xl/7'>Hi, {identity.user.name}</span>
                  <Text variant='largeTitle'>Welcome to your</Text>
                  <Text variant='largeTitle' className='text-gray-alpha-10'>
                    Flatfile {trialEnabled ? 'trial' : ''} account
                  </Text>
                  {trialEnabled && <span className='text-muted text-sm/7'>Trial ends: {trialEndDateText}</span>}
                </div>
                <div
                  className='aspect-video w-full bg-black-opacity-1 bg-cover bg-center'
                  style={{
                    backgroundImage: `url('https://fast.wistia.com/embed/medias/4gm8qtedon/swatch')`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <WistiaPlayer mediaId='4gm8qtedon' />
                </div>
              </div>
              <div className='flex flex-col grow basis-full'>
                <div className='mb-6'>
                  <Text variant='title3' as='label' emphasized htmlFor='company-name' className='block mb-2'>
                    Company*
                  </Text>
                  <Input
                    id='company-name'
                    placeholder='Your company name'
                    value={companyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
                    onBlur={handleBlur}
                    className={`w-full bg-base-white rounded-xs ${isCompanyInvalid ? 'border-destructive' : ''}`}
                    size='large'
                  />
                  {isCompanyInvalid && (
                    <Text variant='callout' className='text-destructive mt-1'>
                      Company name is required
                    </Text>
                  )}
                </div>

                <div className='mb-8'>
                  <div className='mb-2'>
                    <Text variant='title3' emphasized>
                      What do you want to do next
                    </Text>
                  </div>
                  <div className='space-y-2'>
                    {demoApp && (
                      <button
                        type='button'
                        className={`flex items-center w-full p-3 bg-base-white rounded-xs text-left ${
                          selectedOption === 'demo' ? 'ring-2 ring-primary' : ''
                        }`}
                        onClick={() => handleChangePath('demo')}
                      >
                        <Text variant='body'>Explore the custom demo you've seen further.</Text>
                      </button>
                    )}

                    <button
                      type='button'
                      className={`flex items-center w-full p-3 bg-base-white rounded-xs text-left ${
                        selectedOption === 'template' ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => handleChangePath('template')}
                    >
                      <Text variant='body'>Start with an existing template or project file.</Text>
                    </button>

                    <button
                      type='button'
                      className={`flex items-center w-full p-3 bg-base-white rounded-xs text-left ${
                        selectedOption === 'sample' ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => handleChangePath('sample')}
                    >
                      <Text variant='body'>Play with realistic sample data.</Text>
                    </button>
                  </div>
                </div>

                {errorMessage && (
                  <div className='mb-4'>
                    <Text variant='callout' className='text-destructive'>
                      {errorMessage}
                    </Text>
                  </div>
                )}

                <div className='grow basis-full flex flex-col justify-end gap-2'>
                  <label className='mt-3 flex items-start gap-2.5'>
                    <Checkbox checked={isAgreedToTerms} onCheckedChange={() => setIsAgreedToTerms((prev) => !prev)} />
                    <Text className='text-muted'>
                      I have read and agree to{' '}
                      <a
                        className='text-base-black'
                        href='https://flatfile.com/terms/'
                        target='_blank'
                        rel='noreferrer'
                      >
                        User Agreement
                      </a>{' '}
                      and{' '}
                      <a
                        className='text-base-black'
                        href='https://flatfile.com/privacy/'
                        target='_blank'
                        rel='noreferrer'
                      >
                        Privacy Policy
                      </a>
                    </Text>
                  </label>
                  <Button
                    onClick={handleContinue}
                    className='w-full rounded-xs'
                    size='lg'
                    disabled={!companyName || isLoading || !isAgreedToTerms}
                    loading={isLoading}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FirstLoginPage
